import React, { useState } from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Tabs } from '../components/ui/Tabs';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Ticket } from '../types';
import { Plus, Clock, User, AlertCircle, CheckCircle2, Bot, MessageSquare, Phone, Database, TrendingUp, FileText, ThumbsUp, ThumbsDown, ArrowRight } from 'lucide-react';
interface AIConversation {
  id: string;
  customerName: string;
  channel: 'chat' | 'voice';
  topic: string;
  resolved: boolean;
  satisfaction?: 'positive' | 'negative';
  duration: string;
  timestamp: string;
  summary: string;
  escalatedTicketId?: string;
  transcript: Array<{
    speaker: 'customer' | 'ai';
    message: string;
    timestamp: string;
  }>;
}
interface KnowledgeBaseArticle {
  id: string;
  title: string;
  category: string;
  views: number;
  helpfulVotes: number;
  lastUpdated: string;
  status: 'published' | 'draft';
}
const mockTickets: Ticket[] = [{
  id: '1001',
  title: 'Server nicht erreichbar',
  description: 'Der Fileserver antwortet nicht auf Pings.',
  client: 'Zahnarztpraxis Dr. Weber',
  priority: 'critical',
  status: 'open',
  assignedTo: 'Max Mustermann',
  createdAt: '2023-10-25 09:00'
}, {
  id: '1002',
  title: 'Drucker Papierstau',
  description: 'HP LaserJet im Flur meldet Fehler.',
  client: 'Zahnarztpraxis Dr. Hoffmann',
  priority: 'low',
  status: 'in_progress',
  assignedTo: 'Lisa Schmidt',
  createdAt: '2023-10-25 10:30'
}, {
  id: '1003',
  title: 'VPN Verbindung bricht ab',
  description: 'Home Office Mitarbeiter haben Verbindungsprobleme.',
  client: 'Kanzlei Schmidt & Partner',
  priority: 'high',
  status: 'open',
  createdAt: '2023-10-25 11:15'
}, {
  id: '1004',
  title: 'Neuen Benutzer anlegen',
  description: 'Frau Weber fängt am 01.11. an.',
  client: 'Rechtsanwälte Becker & Koch',
  priority: 'medium',
  status: 'waiting',
  assignedTo: 'Max Mustermann',
  createdAt: '2023-10-24 14:00'
}, {
  id: '1005',
  title: 'Outlook startet nicht',
  description: 'Fehlermeldung beim Starten von Outlook.',
  client: 'Kanzlei Schmidt & Partner',
  priority: 'medium',
  status: 'resolved',
  assignedTo: 'Lisa Schmidt',
  createdAt: '2023-10-24 09:30'
}];
const mockAIConversations: AIConversation[] = [{
  id: 'ai-001',
  customerName: 'Dr. Müller',
  channel: 'chat',
  topic: 'Passwort zurücksetzen',
  resolved: true,
  satisfaction: 'positive',
  duration: '3:24',
  timestamp: '2024-01-15 14:30',
  summary: 'Kunde konnte erfolgreich durch Self-Service Passwort-Reset geführt werden.',
  transcript: [{
    speaker: 'customer',
    message: 'Hallo, ich habe mein Passwort vergessen und kann mich nicht mehr anmelden.',
    timestamp: '14:30:12'
  }, {
    speaker: 'ai',
    message: 'Guten Tag! Ich helfe Ihnen gerne beim Zurücksetzen Ihres Passworts. Können Sie mir bitte Ihre E-Mail-Adresse nennen?',
    timestamp: '14:30:15'
  }, {
    speaker: 'customer',
    message: 'mueller@praxis-mueller.de',
    timestamp: '14:30:28'
  }, {
    speaker: 'ai',
    message: 'Vielen Dank. Ich habe Ihnen soeben einen Link zum Zurücksetzen des Passworts an mueller@praxis-mueller.de gesendet. Bitte prüfen Sie Ihr Postfach.',
    timestamp: '14:30:32'
  }, {
    speaker: 'customer',
    message: 'Perfekt, ich habe die E-Mail erhalten und konnte mein Passwort erfolgreich zurücksetzen. Vielen Dank!',
    timestamp: '14:33:15'
  }, {
    speaker: 'ai',
    message: 'Das freut mich! Gibt es noch etwas, wobei ich Ihnen helfen kann?',
    timestamp: '14:33:18'
  }, {
    speaker: 'customer',
    message: 'Nein, das war alles. Danke!',
    timestamp: '14:33:36'
  }]
}, {
  id: 'ai-002',
  customerName: 'Schmidt GmbH',
  channel: 'voice',
  topic: 'Druckerprobleme',
  resolved: true,
  satisfaction: 'positive',
  duration: '5:12',
  timestamp: '2024-01-15 13:45',
  summary: 'Druckertreiber-Neuinstallation per Anleitung durchgeführt. Problem gelöst.',
  transcript: [{
    speaker: 'customer',
    message: 'Unser Drucker druckt seit heute Morgen nicht mehr. Es kommt immer eine Fehlermeldung.',
    timestamp: '13:45:08'
  }, {
    speaker: 'ai',
    message: 'Ich verstehe. Welche Fehlermeldung wird angezeigt?',
    timestamp: '13:45:12'
  }, {
    speaker: 'customer',
    message: 'Es steht "Drucker offline" obwohl er eingeschaltet ist.',
    timestamp: '13:45:24'
  }, {
    speaker: 'ai',
    message: 'Okay, das klingt nach einem Treiberproblem. Ich führe Sie durch die Neuinstallation. Öffnen Sie bitte die Systemsteuerung und gehen Sie zu Geräte und Drucker.',
    timestamp: '13:45:28'
  }, {
    speaker: 'customer',
    message: 'Okay, ich bin dort.',
    timestamp: '13:46:15'
  }, {
    speaker: 'ai',
    message: 'Perfekt. Klicken Sie mit der rechten Maustaste auf Ihren Drucker und wählen Sie "Gerät entfernen".',
    timestamp: '13:46:18'
  }, {
    speaker: 'customer',
    message: 'Erledigt. Was nun?',
    timestamp: '13:47:02'
  }, {
    speaker: 'ai',
    message: 'Jetzt klicken Sie oben auf "Drucker hinzufügen" und folgen Sie dem Assistenten. Der Drucker sollte automatisch erkannt werden.',
    timestamp: '13:47:06'
  }, {
    speaker: 'customer',
    message: 'Super, der Drucker wurde gefunden und installiert. Ich probiere mal einen Testdruck... Es funktioniert! Vielen Dank!',
    timestamp: '13:50:20'
  }]
}, {
  id: 'ai-003',
  customerName: 'Praxis Weber',
  channel: 'chat',
  topic: 'VPN Konfiguration',
  resolved: false,
  satisfaction: 'negative',
  duration: '8:45',
  timestamp: '2024-01-15 12:20',
  summary: 'Komplexes Netzwerkproblem. Kunde benötigt Expertenunterstützung.',
  escalatedTicketId: '1003',
  transcript: [{
    speaker: 'customer',
    message: 'Unsere VPN-Verbindung bricht ständig ab. Wir können nicht von zu Hause arbeiten.',
    timestamp: '12:20:15'
  }, {
    speaker: 'ai',
    message: 'Das tut mir leid zu hören. Lassen Sie uns das Problem gemeinsam lösen. Welches Betriebssystem verwenden Sie?',
    timestamp: '12:20:18'
  }, {
    speaker: 'customer',
    message: 'Windows 11',
    timestamp: '12:20:35'
  }, {
    speaker: 'ai',
    message: 'Danke. Können Sie mir sagen, nach wie vielen Minuten die Verbindung abbricht?',
    timestamp: '12:20:38'
  }, {
    speaker: 'customer',
    message: 'Unterschiedlich, manchmal nach 5 Minuten, manchmal nach 30 Minuten.',
    timestamp: '12:21:12'
  }, {
    speaker: 'ai',
    message: 'Haben Sie bereits versucht, die VPN-Software neu zu installieren?',
    timestamp: '12:21:16'
  }, {
    speaker: 'customer',
    message: 'Ja, das haben wir schon mehrfach gemacht. Das Problem besteht weiterhin.',
    timestamp: '12:22:45'
  }, {
    speaker: 'ai',
    message: 'Ich verstehe. Haben mehrere Mitarbeiter das gleiche Problem oder nur einzelne?',
    timestamp: '12:22:50'
  }, {
    speaker: 'customer',
    message: 'Alle Home-Office Mitarbeiter sind betroffen. Das ist wirklich dringend!',
    timestamp: '12:24:18'
  }, {
    speaker: 'ai',
    message: 'Das klingt nach einem komplexeren Netzwerkproblem, das eine detaillierte Analyse erfordert. Ich erstelle ein Ticket für unser Expertenteam, die sich umgehend darum kümmern werden. Sie werden in Kürze kontaktiert.',
    timestamp: '12:24:25'
  }, {
    speaker: 'customer',
    message: 'Okay, bitte schnell. Wir können so nicht arbeiten.',
    timestamp: '12:29:00'
  }]
}, {
  id: 'ai-004',
  customerName: 'Kanzlei Hoffmann',
  channel: 'voice',
  topic: 'E-Mail Einrichtung',
  resolved: true,
  satisfaction: 'positive',
  duration: '4:30',
  timestamp: '2024-01-15 11:10',
  summary: 'Outlook-Konto erfolgreich auf neuem Gerät eingerichtet.',
  transcript: [{
    speaker: 'customer',
    message: 'Ich habe ein neues Tablet und möchte meine E-Mails darauf einrichten.',
    timestamp: '11:10:05'
  }, {
    speaker: 'ai',
    message: 'Gerne helfe ich Ihnen dabei. Haben Sie Outlook bereits auf dem Tablet installiert?',
    timestamp: '11:10:09'
  }, {
    speaker: 'customer',
    message: 'Ja, die App ist installiert.',
    timestamp: '11:10:22'
  }, {
    speaker: 'ai',
    message: 'Perfekt. Öffnen Sie bitte die Outlook App und tippen Sie auf "Konto hinzufügen".',
    timestamp: '11:10:25'
  }, {
    speaker: 'customer',
    message: 'Okay, gemacht.',
    timestamp: '11:10:45'
  }, {
    speaker: 'ai',
    message: 'Geben Sie nun Ihre E-Mail-Adresse ein und tippen Sie auf "Weiter".',
    timestamp: '11:10:48'
  }, {
    speaker: 'customer',
    message: 'Es fragt jetzt nach dem Passwort.',
    timestamp: '11:11:30'
  }, {
    speaker: 'ai',
    message: 'Richtig, geben Sie Ihr E-Mail-Passwort ein. Das System wird dann automatisch die Servereinstellungen konfigurieren.',
    timestamp: '11:11:34'
  }, {
    speaker: 'customer',
    message: 'Wunderbar, es funktioniert! Ich sehe jetzt alle meine E-Mails. Vielen Dank für die schnelle Hilfe!',
    timestamp: '11:14:35'
  }]
}];
const mockKnowledgeBase: KnowledgeBaseArticle[] = [{
  id: 'kb-001',
  title: 'Passwort zurücksetzen - Schritt für Schritt',
  category: 'Benutzerverwaltung',
  views: 1247,
  helpfulVotes: 892,
  lastUpdated: '2024-01-10',
  status: 'published'
}, {
  id: 'kb-002',
  title: 'VPN Verbindung einrichten (Windows)',
  category: 'Netzwerk',
  views: 856,
  helpfulVotes: 645,
  lastUpdated: '2024-01-08',
  status: 'published'
}, {
  id: 'kb-003',
  title: 'Drucker Installation und Fehlerbehebung',
  category: 'Hardware',
  views: 723,
  helpfulVotes: 534,
  lastUpdated: '2024-01-12',
  status: 'published'
}, {
  id: 'kb-004',
  title: 'Outlook Konfiguration für mobile Geräte',
  category: 'E-Mail',
  views: 612,
  helpfulVotes: 478,
  lastUpdated: '2024-01-05',
  status: 'published'
}];
interface TicketManagementProps {
  onLogout: () => void;
}
export function TicketManagement({
  onLogout
}: TicketManagementProps) {
  const [filter, setFilter] = useState<'all' | 'open' | 'my'>('all');
  const [activeTab, setActiveTab] = useState('tickets');
  const [selectedConversation, setSelectedConversation] = useState<AIConversation | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeBaseArticle | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const getPriorityBadge = (priority: Ticket['priority']) => {
    switch (priority) {
      case 'critical':
        return <Badge variant="danger" data-id="element-1290">Kritisch</Badge>;
      case 'high':
        return <Badge variant="warning" data-id="element-1291">Hoch</Badge>;
      case 'medium':
        return <Badge variant="info" data-id="element-1292">Mittel</Badge>;
      case 'low':
        return <Badge variant="secondary" data-id="element-1293">Niedrig</Badge>;
    }
  };
  const getStatusIcon = (status: Ticket['status']) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="h-5 w-5 text-red-500" data-id="element-1294" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-blue-500" data-id="element-1295" />;
      case 'resolved':
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" data-id="element-1296" />;
      default:
        return <Clock className="h-5 w-5 text-slate-400" data-id="element-1297" />;
    }
  };
  const aiStats = {
    totalConversations: mockAIConversations.length,
    resolvedByAI: mockAIConversations.filter(c => c.resolved).length,
    escalated: mockAIConversations.filter(c => !c.resolved).length,
    avgResolutionTime: '4:32',
    satisfactionRate: 75
  };
  return <MainLayout title="IT-Service Management" onLogout={onLogout} data-id="element-1298">
      {/* Tabs */}
      <div className="mb-6" data-id="element-1299">
        <Tabs tabs={[{
        id: 'tickets',
        label: 'Tickets'
      }, {
        id: 'ai-support',
        label: 'AI Assisted Support'
      }]} activeTab={activeTab} onChange={setActiveTab} data-id="element-1300" />
      </div>

      {/* Tickets Tab */}
      {activeTab === 'tickets' && <>
          <div className="flex justify-between items-center mb-6" data-id="element-1301">
            <div className="flex gap-2" data-id="element-1302">
              <Button variant={filter === 'all' ? 'primary' : 'secondary'} onClick={() => setFilter('all')} size="sm" data-id="element-1303">
                Alle Tickets
              </Button>
              <Button variant={filter === 'open' ? 'primary' : 'secondary'} onClick={() => setFilter('open')} size="sm" data-id="element-1304">
                Offen
              </Button>
              <Button variant={filter === 'my' ? 'primary' : 'secondary'} onClick={() => setFilter('my')} size="sm" data-id="element-1305">
                Meine Tickets
              </Button>
            </div>
            <Button leftIcon={<Plus className="h-4 w-4" data-id="element-1307" />} data-id="element-1306">
              Neues Ticket
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4" data-id="element-1308">
            {mockTickets.map(ticket => <Card key={ticket.id} className="hover:shadow-md transition-shadow cursor-pointer" data-id="element-1309">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center" data-id="element-1310">
                  <div className="flex items-start gap-4" data-id="element-1311">
                    <div className="mt-1" data-id="element-1312">{getStatusIcon(ticket.status)}</div>
                    <div data-id="element-1313">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1314">
                        <span className="text-sm font-mono text-slate-500" data-id="element-1315">
                          #{ticket.id}
                        </span>
                        <h3 className="font-semibold text-slate-900" data-id="element-1316">
                          {ticket.title}
                        </h3>
                        {getPriorityBadge(ticket.priority)}
                      </div>
                      <p className="text-sm text-slate-600 mb-2" data-id="element-1317">
                        {ticket.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-1318">
                        <span className="flex items-center gap-1" data-id="element-1319">
                          <User className="h-3 w-3" data-id="element-1320" />
                          {ticket.client}
                        </span>
                        <span data-id="element-1321">•</span>
                        <span data-id="element-1322">Erstellt: {ticket.createdAt}</span>
                        {ticket.assignedTo && <>
                            <span data-id="element-1323">•</span>
                            <span className="text-teal-600 font-medium" data-id="element-1324">
                              Zugewiesen an: {ticket.assignedTo}
                            </span>
                          </>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end md:self-center" data-id="element-1325">
                    <Button variant="ghost" size="sm" data-id="element-1326">
                      Bearbeiten
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setSelectedTicket(ticket)} data-id="element-1327">
                      Details
                    </Button>
                  </div>
                </div>
              </Card>)}
          </div>
        </>}

      {/* AI Assisted Support Tab */}
      {activeTab === 'ai-support' && <>
          {/* AI Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8" data-id="element-1328">
            <Card data-id="element-1329">
              <div className="flex items-center justify-between" data-id="element-1330">
                <div data-id="element-1331">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-1332">Gespräche heute</p>
                  <p className="text-2xl font-bold text-slate-900" data-id="element-1333">
                    {aiStats.totalConversations}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg" data-id="element-1334">
                  <Bot className="h-6 w-6 text-blue-600" data-id="element-1335" />
                </div>
              </div>
            </Card>

            <Card data-id="element-1336">
              <div className="flex items-center justify-between" data-id="element-1337">
                <div data-id="element-1338">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-1339">Von AI gelöst</p>
                  <p className="text-2xl font-bold text-slate-900" data-id="element-1340">
                    {aiStats.resolvedByAI}
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg" data-id="element-1341">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" data-id="element-1342" />
                </div>
              </div>
            </Card>

            <Card data-id="element-1343">
              <div className="flex items-center justify-between" data-id="element-1344">
                <div data-id="element-1345">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-1346">Eskaliert</p>
                  <p className="text-2xl font-bold text-slate-900" data-id="element-1347">
                    {aiStats.escalated}
                  </p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg" data-id="element-1348">
                  <ArrowRight className="h-6 w-6 text-amber-600" data-id="element-1349" />
                </div>
              </div>
            </Card>

            <Card data-id="element-1350">
              <div className="flex items-center justify-between" data-id="element-1351">
                <div data-id="element-1352">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-1353">Ø Lösungszeit</p>
                  <p className="text-2xl font-bold text-slate-900" data-id="element-1354">
                    {aiStats.avgResolutionTime}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg" data-id="element-1355">
                  <Clock className="h-6 w-6 text-purple-600" data-id="element-1356" />
                </div>
              </div>
            </Card>

            <Card data-id="element-1357">
              <div className="flex items-center justify-between" data-id="element-1358">
                <div data-id="element-1359">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-1360">Zufriedenheit</p>
                  <p className="text-2xl font-bold text-slate-900" data-id="element-1361">
                    {aiStats.satisfactionRate}%
                  </p>
                </div>
                <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1362">
                  <TrendingUp className="h-6 w-6 text-teal-600" data-id="element-1363" />
                </div>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1364">
            {/* AI Conversations */}
            <Card title="AI Bot Konversationen" action={<Button variant="outline" size="sm" data-id="element-1366">
                  Alle anzeigen
                </Button>} data-id="element-1365">
              <div className="space-y-3" data-id="element-1367">
                {mockAIConversations.map(conv => <div key={conv.id} onClick={() => setSelectedConversation(conv)} className="border border-slate-200 rounded-lg p-4 hover:border-teal-300 transition-colors cursor-pointer" data-id="element-1368">
                    <div className="flex items-start justify-between mb-2" data-id="element-1369">
                      <div className="flex items-center gap-2" data-id="element-1370">
                        <div className={`p-2 rounded-lg ${conv.channel === 'chat' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`} data-id="element-1371">
                          {conv.channel === 'chat' ? <MessageSquare className="h-4 w-4" data-id="element-1372" /> : <Phone className="h-4 w-4" data-id="element-1373" />}
                        </div>
                        <div data-id="element-1374">
                          <div className="flex items-center gap-2" data-id="element-1375">
                            <h4 className="font-semibold text-slate-900 text-sm" data-id="element-1376">
                              {conv.customerName}
                            </h4>
                            <Badge variant={conv.channel === 'chat' ? 'info' : 'secondary'} data-id="element-1377">
                              {conv.channel === 'chat' ? 'Chat' : 'Voice'}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500" data-id="element-1378">{conv.topic}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1379">
                        {conv.resolved ? <Badge variant="success" data-id="element-1380">
                            <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-1381" />
                            Gelöst
                          </Badge> : <Badge variant="warning" data-id="element-1382">
                            <ArrowRight className="h-3 w-3 mr-1" data-id="element-1383" />
                            Eskaliert
                          </Badge>}
                      </div>
                    </div>

                    <p className="text-sm text-slate-600 mb-3" data-id="element-1384">
                      {conv.summary}
                    </p>

                    <div className="flex items-center justify-between text-xs text-slate-500" data-id="element-1385">
                      <div className="flex items-center gap-3" data-id="element-1386">
                        <span data-id="element-1387">{conv.timestamp}</span>
                        <span data-id="element-1388">•</span>
                        <span data-id="element-1389">Dauer: {conv.duration}</span>
                      </div>
                      {conv.satisfaction && <div className="flex items-center gap-1" data-id="element-1390">
                          {conv.satisfaction === 'positive' ? <ThumbsUp className="h-3 w-3 text-emerald-600" data-id="element-1391" /> : <ThumbsDown className="h-3 w-3 text-red-600" data-id="element-1392" />}
                        </div>}
                    </div>

                    {conv.escalatedTicketId && <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-1393">
                        <div className="flex items-center justify-between" data-id="element-1394">
                          <span className="text-xs text-slate-600" data-id="element-1395">
                            Ticket erstellt: #{conv.escalatedTicketId}
                          </span>
                          <Button variant="ghost" size="sm" onClick={e => {
                    e.stopPropagation();
                    const ticket = mockTickets.find(t => t.id === conv.escalatedTicketId);
                    if (ticket) {
                      setSelectedConversation(null);
                      setSelectedTicket(ticket);
                    }
                  }} data-id="element-1396">
                            Ticket öffnen
                          </Button>
                        </div>
                      </div>}
                  </div>)}
              </div>
            </Card>

            {/* Knowledge Base */}
            <Card title="Wissensdatenbank" action={<Button variant="outline" size="sm" leftIcon={<Plus className="h-4 w-4" data-id="element-1399" />} data-id="element-1398">
                  Neuer Artikel
                </Button>} data-id="element-1397">
              <div className="space-y-3" data-id="element-1400">
                {mockKnowledgeBase.map(article => <div key={article.id} className="border border-slate-200 rounded-lg p-4 hover:border-teal-300 transition-colors cursor-pointer" data-id="element-1401">
                    <div className="flex items-start justify-between mb-2" data-id="element-1402">
                      <div className="flex items-start gap-3" data-id="element-1403">
                        <div className="p-2 bg-teal-100 text-teal-600 rounded-lg" data-id="element-1404">
                          <FileText className="h-4 w-4" data-id="element-1405" />
                        </div>
                        <div data-id="element-1406">
                          <h4 className="font-semibold text-slate-900 text-sm mb-1" data-id="element-1407">
                            {article.title}
                          </h4>
                          <Badge variant="secondary" data-id="element-1408">{article.category}</Badge>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mt-3 text-center" data-id="element-1409">
                      <div className="bg-slate-50 rounded p-2" data-id="element-1410">
                        <p className="text-xs text-slate-500" data-id="element-1411">Aufrufe</p>
                        <p className="font-semibold text-slate-900 text-sm" data-id="element-1412">
                          {article.views}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2" data-id="element-1413">
                        <p className="text-xs text-slate-500" data-id="element-1414">Hilfreich</p>
                        <p className="font-semibold text-slate-900 text-sm" data-id="element-1415">
                          {article.helpfulVotes}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2" data-id="element-1416">
                        <p className="text-xs text-slate-500" data-id="element-1417">Erfolgsrate</p>
                        <p className="font-semibold text-slate-900 text-sm" data-id="element-1418">
                          {Math.round(article.helpfulVotes / article.views * 100)}
                          %
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100" data-id="element-1419">
                      <span className="text-xs text-slate-500" data-id="element-1420">
                        Aktualisiert: {article.lastUpdated}
                      </span>
                      <div className="flex gap-2" data-id="element-1421">
                        <Button variant="ghost" size="sm" onClick={e => {
                    e.stopPropagation();
                    setSelectedArticle(article);
                  }} data-id="element-1422">
                          Bearbeiten
                        </Button>
                        <Button variant="outline" size="sm" data-id="element-1423">
                          Anzeigen
                        </Button>
                      </div>
                    </div>
                  </div>)}
              </div>
            </Card>
          </div>
        </>}

      {/* Conversation Detail Modal */}
      {selectedConversation && <Modal isOpen={!!selectedConversation} onClose={() => setSelectedConversation(null)} title="Konversationsdetails" maxWidth="xl" data-id="element-1424">
          <div className="space-y-4" data-id="element-1425">
            {/* Header Info */}
            <div className="flex items-start justify-between pb-4 border-b border-slate-200" data-id="element-1426">
              <div data-id="element-1427">
                <div className="flex items-center gap-3 mb-2" data-id="element-1428">
                  <div className={`p-2 rounded-lg ${selectedConversation.channel === 'chat' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`} data-id="element-1429">
                    {selectedConversation.channel === 'chat' ? <MessageSquare className="h-5 w-5" data-id="element-1430" /> : <Phone className="h-5 w-5" data-id="element-1431" />}
                  </div>
                  <div data-id="element-1432">
                    <h3 className="font-semibold text-slate-900" data-id="element-1433">
                      {selectedConversation.customerName}
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-1434">
                      {selectedConversation.topic}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-1435">
                  <Badge variant={selectedConversation.channel === 'chat' ? 'info' : 'secondary'} data-id="element-1436">
                    {selectedConversation.channel === 'chat' ? 'Chat' : 'Voice'}
                  </Badge>
                  {selectedConversation.resolved ? <Badge variant="success" data-id="element-1437">
                      <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-1438" />
                      Gelöst
                    </Badge> : <Badge variant="warning" data-id="element-1439">
                      <ArrowRight className="h-3 w-3 mr-1" data-id="element-1440" />
                      Eskaliert
                    </Badge>}
                  {selectedConversation.satisfaction && <Badge variant={selectedConversation.satisfaction === 'positive' ? 'success' : 'danger'} data-id="element-1441">
                      {selectedConversation.satisfaction === 'positive' ? <ThumbsUp className="h-3 w-3 mr-1" data-id="element-1442" /> : <ThumbsDown className="h-3 w-3 mr-1" data-id="element-1443" />}
                      {selectedConversation.satisfaction === 'positive' ? 'Positiv' : 'Negativ'}
                    </Badge>}
                </div>
              </div>
              <div className="text-right text-sm text-slate-500" data-id="element-1444">
                <p data-id="element-1445">{selectedConversation.timestamp}</p>
                <p data-id="element-1446">Dauer: {selectedConversation.duration}</p>
              </div>
            </div>

            {/* Transcript */}
            <div className="space-y-3 max-h-96 overflow-y-auto" data-id="element-1447">
              {selectedConversation.transcript.map((message, idx) => <div key={idx} className={`flex gap-3 ${message.speaker === 'customer' ? 'flex-row' : 'flex-row-reverse'}`} data-id="element-1448">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${message.speaker === 'customer' ? 'bg-slate-200 text-slate-700' : 'bg-teal-100 text-teal-700'}`} data-id="element-1449">
                    {message.speaker === 'customer' ? <User className="h-4 w-4" data-id="element-1450" /> : <Bot className="h-4 w-4" data-id="element-1451" />}
                  </div>
                  <div className={`flex-1 ${message.speaker === 'customer' ? 'text-left' : 'text-right'}`} data-id="element-1452">
                    <div className={`inline-block max-w-[80%] rounded-lg px-4 py-2 ${message.speaker === 'customer' ? 'bg-slate-100 text-slate-900' : 'bg-teal-600 text-white'}`} data-id="element-1453">
                      <p className="text-sm" data-id="element-1454">{message.message}</p>
                    </div>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-1455">
                      {message.timestamp}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Escalation Info */}
            {selectedConversation.escalatedTicketId && <div className="mt-4 pt-4 border-t border-slate-200" data-id="element-1456">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-1457">
                  <div className="flex items-center justify-between" data-id="element-1458">
                    <div data-id="element-1459">
                      <p className="text-sm font-medium text-amber-900" data-id="element-1460">
                        Konversation wurde eskaliert
                      </p>
                      <p className="text-sm text-amber-700 mt-1" data-id="element-1461">
                        Ticket #{selectedConversation.escalatedTicketId} wurde
                        erstellt
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => {
                const ticket = mockTickets.find(t => t.id === selectedConversation.escalatedTicketId);
                if (ticket) {
                  setSelectedConversation(null);
                  setSelectedTicket(ticket);
                }
              }} data-id="element-1462">
                      Ticket öffnen
                    </Button>
                  </div>
                </div>
              </div>}
          </div>
        </Modal>}

      {/* Knowledge Base Article Editor Modal */}
      {selectedArticle && <Modal isOpen={!!selectedArticle} onClose={() => setSelectedArticle(null)} title="Wissensdatenbank-Artikel bearbeiten" maxWidth="2xl" data-id="element-1463">
          <div className="space-y-6" data-id="element-1464">
            {/* Article Info */}
            <div className="grid grid-cols-2 gap-4" data-id="element-1465">
              <div data-id="element-1466">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1467">
                  Titel
                </label>
                <Input type="text" value={selectedArticle.title} placeholder="Artikel-Titel" data-id="element-1468" />
              </div>
              <div data-id="element-1469">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1470">
                  Kategorie
                </label>
                <Select value={selectedArticle.category} onChange={() => {}} options={[{
              value: 'Benutzerverwaltung',
              label: 'Benutzerverwaltung'
            }, {
              value: 'Netzwerk',
              label: 'Netzwerk'
            }, {
              value: 'Hardware',
              label: 'Hardware'
            }, {
              value: 'E-Mail',
              label: 'E-Mail'
            }, {
              value: 'Software',
              label: 'Software'
            }]} data-id="element-1471" />
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-4" data-id="element-1472">
              <Card data-id="element-1473">
                <div className="text-center" data-id="element-1474">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-1475">Aufrufe</p>
                  <p className="text-2xl font-bold text-slate-900" data-id="element-1476">
                    {selectedArticle.views}
                  </p>
                </div>
              </Card>
              <Card data-id="element-1477">
                <div className="text-center" data-id="element-1478">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-1479">Hilfreich-Votes</p>
                  <p className="text-2xl font-bold text-slate-900" data-id="element-1480">
                    {selectedArticle.helpfulVotes}
                  </p>
                </div>
              </Card>
              <Card data-id="element-1481">
                <div className="text-center" data-id="element-1482">
                  <p className="text-sm text-slate-500 mb-1" data-id="element-1483">Erfolgsrate</p>
                  <p className="text-2xl font-bold text-emerald-600" data-id="element-1484">
                    {Math.round(selectedArticle.helpfulVotes / selectedArticle.views * 100)}
                    %
                  </p>
                </div>
              </Card>
            </div>

            {/* Content Editor */}
            <div data-id="element-1485">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1486">
                Artikel-Inhalt
              </label>
              <textarea className="w-full h-64 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none font-mono text-sm" placeholder="Artikel-Inhalt in Markdown..." defaultValue={selectedArticle.id === 'kb-001' ? `# Passwort zurücksetzen - Schritt für Schritt

## Problem
Sie haben Ihr Passwort vergessen und können sich nicht mehr an Ihrem Konto anmelden.

## Lösung
Folgen Sie dieser Schritt-für-Schritt Anleitung:

1. Öffnen Sie die Login-Seite Ihres Systems
2. Klicken Sie auf "Passwort vergessen?"
3. Geben Sie Ihre E-Mail-Adresse ein
4. Prüfen Sie Ihr E-Mail-Postfach (auch Spam-Ordner)
5. Klicken Sie auf den Link in der E-Mail
6. Geben Sie Ihr neues Passwort ein (mind. 8 Zeichen, Groß-/Kleinbuchstaben, Zahlen)
7. Bestätigen Sie das neue Passwort
8. Melden Sie sich mit dem neuen Passwort an

## Häufige Fehler
- **E-Mail kommt nicht an**: Prüfen Sie den Spam-Ordner und warten Sie 5-10 Minuten
- **Link ist abgelaufen**: Links sind nur 24 Stunden gültig - fordern Sie einen neuen an
- **Passwort wird nicht akzeptiert**: Achten Sie auf die Mindestanforderungen

## Verwandte Artikel
- [Zwei-Faktor-Authentifizierung einrichten](#)
- [Sicheres Passwort erstellen](#)

## Tags
#passwort #zurücksetzen #login #benutzerverwaltung #sicherheit` : selectedArticle.id === 'kb-002' ? `# VPN Verbindung einrichten (Windows)

## Problem
Sie möchten von zu Hause oder unterwegs sicher auf das Firmennetzwerk zugreifen.

## Lösung
Windows VPN einrichten:

1. Öffnen Sie die Windows-Einstellungen (Windows + I)
2. Navigieren Sie zu "Netzwerk und Internet"
3. Klicken Sie auf "VPN" im linken Menü
4. Klicken Sie auf "VPN-Verbindung hinzufügen"
5. Geben Sie folgende Daten ein:
   - VPN-Anbieter: Windows (integriert)
   - Verbindungsname: Firmen-VPN
   - Servername: vpn.ihre-firma.de
   - VPN-Typ: IKEv2
   - Benutzername: Ihr Firmen-Login
   - Passwort: Ihr Firmen-Passwort
6. Klicken Sie auf "Speichern"
7. Klicken Sie auf die neue VPN-Verbindung und dann auf "Verbinden"

## Häufige Fehler
- **Verbindung schlägt fehl**: Prüfen Sie Ihre Internetverbindung und die Serveradresse
- **Authentifizierung fehlgeschlagen**: Überprüfen Sie Benutzername und Passwort
- **Verbindung bricht ab**: Firewall-Einstellungen können VPN blockieren

## Verwandte Artikel
- [VPN auf Mac einrichten](#)
- [VPN auf Mobilgeräten](#)
- [Netzwerkprobleme beheben](#)

## Tags
#vpn #netzwerk #remote #homeoffice #sicherheit` : selectedArticle.id === 'kb-003' ? `# Drucker Installation und Fehlerbehebung

## Problem
Ihr Drucker wird nicht erkannt, druckt nicht oder zeigt Fehlermeldungen an.

## Lösung - Drucker installieren
Neue Drucker einrichten:

1. Schließen Sie den Drucker per USB an oder verbinden Sie ihn mit dem WLAN
2. Öffnen Sie die Systemsteuerung
3. Gehen Sie zu "Geräte und Drucker"
4. Klicken Sie auf "Drucker hinzufügen"
5. Warten Sie, bis der Drucker erkannt wird
6. Folgen Sie dem Installations-Assistenten
7. Drucken Sie eine Testseite

## Lösung - Häufige Druckerprobleme

### Drucker druckt nicht
1. Prüfen Sie die Verbindung (USB/WLAN)
2. Stellen Sie sicher, dass der Drucker eingeschaltet ist
3. Prüfen Sie den Druckerstatus in "Geräte und Drucker"
4. Löschen Sie die Druckerwarteschlange
5. Starten Sie den Druckerspooler-Dienst neu

### Drucker offline
1. Öffnen Sie "Geräte und Drucker"
2. Rechtsklick auf den Drucker
3. Deaktivieren Sie "Drucker offline verwenden"
4. Setzen Sie den Drucker als Standarddrucker

### Papierstau
1. Schalten Sie den Drucker aus
2. Öffnen Sie alle Klappen
3. Entfernen Sie vorsichtig das Papier
4. Prüfen Sie auf Papierreste
5. Schließen Sie alle Klappen
6. Schalten Sie den Drucker ein

## Verwandte Artikel
- [Druckertreiber aktualisieren](#)
- [Netzwerkdrucker einrichten](#)

## Tags
#drucker #hardware #installation #fehlerbehebung #papierstau` : `# Outlook Konfiguration für mobile Geräte

## Problem
Sie möchten Ihre Firmen-E-Mails auf Ihrem Smartphone oder Tablet empfangen.

## Lösung - iOS (iPhone/iPad)

1. Laden Sie Microsoft Outlook aus dem App Store
2. Öffnen Sie die Outlook App
3. Tippen Sie auf "Konto hinzufügen"
4. Geben Sie Ihre E-Mail-Adresse ein
5. Tippen Sie auf "Konto hinzufügen"
6. Geben Sie Ihr Passwort ein
7. Folgen Sie den Anweisungen zur Zwei-Faktor-Authentifizierung
8. Erlauben Sie die Benachrichtigungen

## Lösung - Android

1. Laden Sie Microsoft Outlook aus dem Play Store
2. Öffnen Sie die Outlook App
3. Tippen Sie auf "Erste Schritte"
4. Geben Sie Ihre E-Mail-Adresse ein
5. Tippen Sie auf "Weiter"
6. Geben Sie Ihr Passwort ein
7. Folgen Sie den Anweisungen zur Zwei-Faktor-Authentifizierung
8. Konfigurieren Sie Ihre Benachrichtigungseinstellungen

## Häufige Fehler
- **Konto kann nicht hinzugefügt werden**: Prüfen Sie Ihre Internetverbindung
- **Passwort wird nicht akzeptiert**: Verwenden Sie Ihr Firmen-Passwort, nicht Ihr Microsoft-Konto
- **E-Mails werden nicht synchronisiert**: Prüfen Sie die Synchronisierungseinstellungen

## Erweiterte Einstellungen
- Push-Benachrichtigungen aktivieren
- Signatur einrichten
- Automatische Antworten konfigurieren
- Kalender synchronisieren

## Verwandte Artikel
- [Outlook auf Desktop einrichten](#)
- [E-Mail-Signatur erstellen](#)
- [Kalender freigeben](#)

## Tags
#outlook #email #mobile #ios #android #konfiguration`} data-id="element-1487" />
            </div>

            {/* AI Training Section */}
            <div className="border-t border-slate-200 pt-6" data-id="element-1488">
              <div className="flex items-center gap-2 mb-4" data-id="element-1489">
                <Bot className="h-5 w-5 text-teal-600" data-id="element-1490" />
                <h3 className="font-semibold text-slate-900" data-id="element-1491">
                  AI Bot Training
                </h3>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4" data-id="element-1492">
                <p className="text-sm text-teal-900 mb-3" data-id="element-1493">
                  Dieser Artikel wird automatisch für das Training des AI
                  Support Bots verwendet. Der Bot kann Kunden basierend auf
                  diesem Inhalt unterstützen.
                </p>
                <div className="flex items-center gap-2" data-id="element-1494">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1495">
                    <input type="checkbox" defaultChecked className="rounded border-teal-300 text-teal-600 focus:ring-teal-500" data-id="element-1496" />
                    <span className="text-sm text-teal-900" data-id="element-1497">
                      Für AI Bot Training aktiviert
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Keywords for AI */}
            <div data-id="element-1498">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1499">
                Schlüsselwörter (für AI-Erkennung)
              </label>
              <div className="flex flex-wrap gap-2 mb-3" data-id="element-1500">
                {['Passwort', 'vergessen', 'zurücksetzen', 'Login', 'Anmeldung', 'Zugang'].map(keyword => <span key={keyword} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2" data-id="element-1501">
                    {keyword}
                    <button className="hover:text-blue-900" data-id="element-1502">×</button>
                  </span>)}
              </div>
              <Input type="text" placeholder="Neues Schlüsselwort hinzufügen..." data-id="element-1503" />
            </div>

            {/* Status and Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200" data-id="element-1504">
              <div className="flex items-center gap-2" data-id="element-1505">
                <label className="text-sm font-medium text-slate-700" data-id="element-1506">
                  Status:
                </label>
                <Badge variant={selectedArticle.status === 'published' ? 'success' : 'secondary'} data-id="element-1507">
                  {selectedArticle.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                </Badge>
              </div>
              <div className="flex gap-2" data-id="element-1508">
                <Button variant="outline" onClick={() => setSelectedArticle(null)} data-id="element-1509">
                  Abbrechen
                </Button>
                <Button variant="secondary" data-id="element-1510">Als Entwurf speichern</Button>
                <Button variant="primary" data-id="element-1511">Veröffentlichen</Button>
              </div>
            </div>
          </div>
        </Modal>}

      {/* Ticket Detail Modal */}
      {selectedTicket && <Modal isOpen={!!selectedTicket} onClose={() => setSelectedTicket(null)} title={`Ticket #${selectedTicket.id}`} maxWidth="2xl" data-id="element-1512">
          <div className="space-y-6" data-id="element-1513">
            {/* Ticket Header */}
            <div className="flex items-start justify-between pb-4 border-b border-slate-200" data-id="element-1514">
              <div className="flex-1" data-id="element-1515">
                <div className="flex items-center gap-3 mb-2" data-id="element-1516">
                  {getStatusIcon(selectedTicket.status)}
                  <h2 className="text-xl font-semibold text-slate-900" data-id="element-1517">
                    {selectedTicket.title}
                  </h2>
                </div>
                <div className="flex items-center gap-2" data-id="element-1518">
                  {getPriorityBadge(selectedTicket.priority)}
                  <Badge variant={selectedTicket.status === 'resolved' ? 'success' : selectedTicket.status === 'in_progress' ? 'info' : selectedTicket.status === 'waiting' ? 'warning' : 'secondary'} data-id="element-1519">
                    {selectedTicket.status === 'open' ? 'Offen' : selectedTicket.status === 'in_progress' ? 'In Bearbeitung' : selectedTicket.status === 'waiting' ? 'Wartend' : 'Gelöst'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Ticket Info Grid */}
            <div className="grid grid-cols-2 gap-4" data-id="element-1520">
              <div data-id="element-1521">
                <label className="block text-sm font-medium text-slate-500 mb-1" data-id="element-1522">
                  Kunde
                </label>
                <div className="flex items-center gap-2" data-id="element-1523">
                  <User className="h-4 w-4 text-slate-400" data-id="element-1524" />
                  <span className="text-slate-900" data-id="element-1525">
                    {selectedTicket.client}
                  </span>
                </div>
              </div>
              <div data-id="element-1526">
                <label className="block text-sm font-medium text-slate-500 mb-1" data-id="element-1527">
                  Erstellt am
                </label>
                <div className="flex items-center gap-2" data-id="element-1528">
                  <Clock className="h-4 w-4 text-slate-400" data-id="element-1529" />
                  <span className="text-slate-900" data-id="element-1530">
                    {selectedTicket.createdAt}
                  </span>
                </div>
              </div>
              {selectedTicket.assignedTo && <div data-id="element-1531">
                  <label className="block text-sm font-medium text-slate-500 mb-1" data-id="element-1532">
                    Zugewiesen an
                  </label>
                  <div className="flex items-center gap-2" data-id="element-1533">
                    <div className="h-6 w-6 rounded-full bg-teal-100 flex items-center justify-center" data-id="element-1534">
                      <span className="text-xs font-semibold text-teal-700" data-id="element-1535">
                        {selectedTicket.assignedTo.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <span className="text-slate-900" data-id="element-1536">
                      {selectedTicket.assignedTo}
                    </span>
                  </div>
                </div>}
            </div>

            {/* Description */}
            <div data-id="element-1537">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1538">
                Beschreibung
              </label>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-1539">
                <p className="text-slate-900" data-id="element-1540">{selectedTicket.description}</p>
              </div>
            </div>

            {/* Detailed Information based on ticket */}
            {selectedTicket.id === '1001' && <div data-id="element-1541">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1542">
                  Technische Details
                </label>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-2" data-id="element-1543">
                  <div className="flex items-start gap-2" data-id="element-1544">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1545">
                      Server:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1546">
                      fileserver-01.local
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1547">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1548">
                      IP-Adresse:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1549">192.168.1.50</span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1550">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1551">
                      Letzter Ping:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1552">
                      Fehlgeschlagen (Timeout nach 5s)
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1553">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1554">
                      Betroffene User:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1555">
                      Alle Praxismitarbeiter (ca. 12 Personen)
                    </span>
                  </div>
                </div>
              </div>}

            {selectedTicket.id === '1002' && <div data-id="element-1556">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1557">
                  Geräte-Information
                </label>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-2" data-id="element-1558">
                  <div className="flex items-start gap-2" data-id="element-1559">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1560">
                      Gerät:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1561">
                      HP LaserJet Pro M404dn
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1562">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1563">
                      Standort:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1564">
                      1. OG, Flur rechts
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1565">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1566">
                      Fehlermeldung:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1567">
                      Paper Jam in Tray 2
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1568">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1569">
                      Bereits versucht:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1570">
                      Papier entfernt, Neustart durchgeführt
                    </span>
                  </div>
                </div>
              </div>}

            {selectedTicket.id === '1003' && <div data-id="element-1571">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1572">
                  VPN-Konfiguration
                </label>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-2" data-id="element-1573">
                  <div className="flex items-start gap-2" data-id="element-1574">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1575">
                      VPN-Typ:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1576">IKEv2</span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1577">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1578">
                      Server:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1579">
                      vpn.kanzlei-schmidt.de
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1580">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1581">
                      Betroffene User:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1582">
                      5 Home-Office Mitarbeiter
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1583">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1584">
                      Symptom:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1585">
                      Verbindung bricht nach 5-30 Minuten ab
                    </span>
                  </div>
                </div>
              </div>}

            {selectedTicket.id === '1004' && <div data-id="element-1586">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1587">
                  Benutzer-Details
                </label>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-2" data-id="element-1588">
                  <div className="flex items-start gap-2" data-id="element-1589">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1590">
                      Name:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1591">
                      Frau Anna Weber
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1592">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1593">
                      Position:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1594">
                      Rechtsanwaltsfachangestellte
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1595">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1596">
                      Startdatum:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1597">01.11.2024</span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1598">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1599">
                      Benötigt:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1600">
                      E-Mail-Konto, VPN-Zugang, Zugriff auf DMS
                    </span>
                  </div>
                </div>
              </div>}

            {selectedTicket.id === '1005' && <div data-id="element-1601">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1602">
                  Fehler-Details
                </label>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-2" data-id="element-1603">
                  <div className="flex items-start gap-2" data-id="element-1604">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1605">
                      Fehlermeldung:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1606">
                      "Outlook kann nicht gestartet werden. Das Outlook-Fenster
                      kann nicht geöffnet werden."
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1607">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1608">
                      Outlook Version:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1609">
                      Microsoft 365 (Version 2310)
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1610">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1611">
                      Betriebssystem:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1612">
                      Windows 11 Pro
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1613">
                    <span className="text-sm font-medium text-slate-600 min-w-32" data-id="element-1614">
                      Lösung:
                    </span>
                    <span className="text-sm text-slate-900" data-id="element-1615">
                      Outlook-Profil neu erstellt, Problem behoben
                    </span>
                  </div>
                </div>
              </div>}

            {/* Activity Timeline */}
            <div data-id="element-1616">
              <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-1617">
                Aktivitätsverlauf
              </label>
              <div className="space-y-3" data-id="element-1618">
                <div className="flex gap-3" data-id="element-1619">
                  <div className="flex flex-col items-center" data-id="element-1620">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-1621">
                      <Plus className="h-4 w-4 text-blue-600" data-id="element-1622" />
                    </div>
                    <div className="w-0.5 h-full bg-slate-200 mt-2" data-id="element-1623"></div>
                  </div>
                  <div className="flex-1 pb-4" data-id="element-1624">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1625">
                      Ticket erstellt
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1626">
                      {selectedTicket.createdAt}
                    </p>
                    <p className="text-sm text-slate-600 mt-1" data-id="element-1627">
                      Von {selectedTicket.client}
                    </p>
                  </div>
                </div>

                {selectedTicket.assignedTo && <div className="flex gap-3" data-id="element-1628">
                    <div className="flex flex-col items-center" data-id="element-1629">
                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center" data-id="element-1630">
                        <User className="h-4 w-4 text-teal-600" data-id="element-1631" />
                      </div>
                      <div className="w-0.5 h-full bg-slate-200 mt-2" data-id="element-1632"></div>
                    </div>
                    <div className="flex-1 pb-4" data-id="element-1633">
                      <p className="text-sm font-medium text-slate-900" data-id="element-1634">
                        Zugewiesen
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-1635">Vor 2 Stunden</p>
                      <p className="text-sm text-slate-600 mt-1" data-id="element-1636">
                        An {selectedTicket.assignedTo}
                      </p>
                    </div>
                  </div>}

                {selectedTicket.status === 'in_progress' && <div className="flex gap-3" data-id="element-1637">
                    <div className="flex flex-col items-center" data-id="element-1638">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-1639">
                        <Clock className="h-4 w-4 text-blue-600" data-id="element-1640" />
                      </div>
                      <div className="w-0.5 h-full bg-slate-200 mt-2" data-id="element-1641"></div>
                    </div>
                    <div className="flex-1 pb-4" data-id="element-1642">
                      <p className="text-sm font-medium text-slate-900" data-id="element-1643">
                        In Bearbeitung
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-1644">Vor 1 Stunde</p>
                      <p className="text-sm text-slate-600 mt-1" data-id="element-1645">
                        Techniker vor Ort
                      </p>
                    </div>
                  </div>}

                {selectedTicket.status === 'resolved' && <div className="flex gap-3" data-id="element-1646">
                    <div className="flex flex-col items-center" data-id="element-1647">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-1648">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" data-id="element-1649" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-1650">
                      <p className="text-sm font-medium text-slate-900" data-id="element-1651">
                        Gelöst
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-1652">Vor 30 Minuten</p>
                      <p className="text-sm text-slate-600 mt-1" data-id="element-1653">
                        Problem behoben und getestet
                      </p>
                    </div>
                  </div>}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200" data-id="element-1654">
              <div className="flex gap-2" data-id="element-1655">
                {selectedTicket.status !== 'resolved' && <>
                    <Button variant="outline" data-id="element-1656">Status ändern</Button>
                    <Button variant="outline" data-id="element-1657">Zuweisen</Button>
                  </>}
              </div>
              <div className="flex gap-2" data-id="element-1658">
                <Button variant="outline" onClick={() => setSelectedTicket(null)} data-id="element-1659">
                  Schließen
                </Button>
                {selectedTicket.status !== 'resolved' && <Button variant="primary" data-id="element-1660">Als gelöst markieren</Button>}
              </div>
            </div>
          </div>
        </Modal>}
    </MainLayout>;
}